import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter } from '../../components/ui/dialog';
import LuminaChatbot from '../../components/lumina/LuminaChatbotWidget';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { AreaChart, Area, LineChart, Line, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Toaster } from '../../components/ui/sonner';
import SendToBillingDialog from '../../components/shared/SendToBillingDialog';
import LensDemoDialog from '../../components/lumina/LensDemoDialog';
import DiseaseBlocksPanel from '../../components/lumina/DiseaseBlocksPanel';
import ClinicalFocusSidebar from '../../components/lumina/ClinicalFocusSidebar';
import SmartPrepCard from '../../components/lumina/SmartPrepCard';
import { useHIPAACopy } from '../../components/shared/HIPAACopy';
import { useAuth } from '../../contexts/AuthContext';
import {
  Camera, Loader2, Sparkles, Stethoscope, AlertTriangle,
  ArrowLeft, Pill, TestTube, Plus, ChevronRight, Heart,
  BarChart3, TrendingUp, Clipboard, Wifi, GripVertical,
  HeartPulse, ClipboardList, ShieldCheck, Zap, Lightbulb,
  Coins, Compass, ArrowUpToLine,
} from 'lucide-react';

import { LuminaService } from '../../services/api';
import NotificationBell from '../../components/shared/NotificationBell';
import getServices from '../../services/serviceRouter';

// ── Extracted modules ──
import {
  API, LUMINA_EMBLEM,
  TODAYS_SCHEDULE, FUTURE_SCHEDULES, ALL_PATIENTS_LIST,
  PATIENTS_DB, buildPatient, getPatient, buildDate,
  LIVE_TRANSCRIPT, FALLBACK_SOAP, SUGGESTED_CODES, CLINICAL_SUGGESTIONS,
  TOUR_STEPS,
} from '../../components/lumina/doctor/doctorModeData';
import {
  getScheduleForSpecialty, getSpecialtyPatient,
} from '../../components/lumina/doctor/doctorModeSpecialty';
import { SpecialtyIntakeCard } from '../../components/lumina/doctor/specialty/SpecialtyIntakeCard';
import { ScheduleLensDialog } from '../../components/lumina/doctor/ScheduleLensDialog';
import { PatientHeaderBar } from '../../components/lumina/doctor/PatientHeaderBar';
import { ChartTabSections } from '../../components/lumina/doctor/ChartTabSections';
import { SOAPPanel } from '../../components/lumina/doctor/SOAPPanel';
import usePatientChartData from '../../hooks/lumina/usePatientChartData';
import { RecordingPanel } from '../../components/lumina/doctor/RecordingPanel';
import { DoctorDialogs, TourOverlay } from '../../components/lumina/doctor/DoctorDialogs';
import { SpecialtySwitcher } from '../../components/lumina/doctor/SpecialtySwitcher';
import { MentalHealthPanel } from '../../components/lumina/doctor/specialty/MentalHealthPanel';
import { OBGYNPanel } from '../../components/lumina/doctor/specialty/OBGYNPanel';
import { PediatricsPanel } from '../../components/lumina/doctor/specialty/PediatricsPanel';
import { SurgicalPanel } from '../../components/lumina/doctor/specialty/SurgicalPanel';
import { useSpecialty } from '../../contexts/SpecialtyContext';
import useDictationRecorder from '../../hooks/lumina/useDictationRecorder';
import { useAppMode } from '../../contexts/AppModeContext';

// ═══ Sortable Card Wrapper for DnD ═══
const SortableCard = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : 'auto', opacity: isDragging ? 0.85 : 1 };
  return (
    <div ref={setNodeRef} style={style} className={cn("relative group", isDragging && "shadow-xl rounded-xl")}>
      <button {...attributes} {...listeners} className="absolute -left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-10 h-8 w-5 flex flex-col items-center justify-center gap-0.5 rounded bg-white/90 border border-slate-200 shadow-sm cursor-grab active:cursor-grabbing" data-testid={`drag-handle-${id}`} aria-label={`Reorder ${id}`}>
        <GripVertical className="h-3 w-3 text-slate-400" />
      </button>
      {children}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ════════════════════════════════════════════════════════════════
const LuminaDoctorMode = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { requestCopy, HIPAADialog } = useHIPAACopy();
  const { authFetch } = useAuth();

  // Schedule
  const [schedule, setSchedule] = useState(TODAYS_SCHEDULE);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [scheduleCollapsed, setScheduleCollapsed] = useState(false);
  const [showScheduleLens, setShowScheduleLens] = useState(false);
  const sidebarMode = searchParams.get('patientId') ? 'patients' : 'schedule';
  const sidebarPatients = sidebarMode === 'patients' ? ALL_PATIENTS_LIST : schedule;

  // Patient
  const [activeApt, setActiveApt] = useState(null);
  const [patient, setPatient] = useState(null);
  const [patientLoading, setPatientLoading] = useState(false);
  const [patientError, setPatientError] = useState(null);

  // Clinical context
  const [chartView, setChartView] = useState('overview');
  const [chartTab, setChartTab] = useState('demographics');
  const [cardOrder, setCardOrder] = useState(['specialty-intake', 'quick-glance', 'vitals', 'risk-care', 'actionable']);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) { setCardOrder(prev => arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id))); }
  };

  // Recording
  const [recordingMode, setRecordingMode] = useState(null);
  const { mode: appMode } = useAppMode();
  const { specialty, specialtyId } = useSpecialty();
  const isDemo = appMode === 'demo';
  const svc = getServices(appMode);

  // Chart document uploads

  // ── Chart data hook (absorbed data, document uploads, absorb) ──
  const { absorbedData, setAbsorbedData, chartUploads, chartFileRef, handleChartUpload, handleChartAbsorb, patientDocuments } = usePatientChartData({ activeApt, patient, setPatient });

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState([]);
  const intervalRef = useRef(null);

  // SOAP
  const [isGenerating, setIsGenerating] = useState(false);
  const [soapContent, setSoapContent] = useState(null);
  const [editingSections, setEditingSections] = useState({});
  const [activeTab, setActiveTab] = useState('soap');

  // Encounter Review
  const [reviewingNote, setReviewingNote] = useState(null);
  const [reviewEdits, setReviewEdits] = useState({});
  const [reviewDictating, setReviewDictating] = useState(null);

  // Dialogs
  const [showLens, setShowLens] = useState(false);
  const [lensDefaultDemo, setLensDefaultDemo] = useState(null);
  const [showBilling, setShowBilling] = useState(false);
  const [showDM, setShowDM] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showCoding, setShowCoding] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [dmRecipient, setDmRecipient] = useState('');
  const [summaryEmail, setSummaryEmail] = useState('');
  const [recordingTab, setRecordingTab] = useState('clinical');
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [clinicalChecklist, setClinicalChecklist] = useState({});

  // Tour state
  const [tourStep, setTourStep] = useState(-1); // -1 = not active
  const [tourDismissed, setTourDismissed] = useState(() => sessionStorage.getItem('cf-tour-dismissed') === 'true');
  const tourActive = tourStep >= 0;

  // Smart Prep state (API-driven)
  const [smartPrep, setSmartPrep] = useState(null);       // prep object for current patient
  const [smartPrepLoading, setSmartPrepLoading] = useState(false);
  const [smartPrepError, setSmartPrepError] = useState(null);

  // FHIR EHR sync state
  const [fhirStatus, setFhirStatus] = useState(null);  // {fhir_enabled, sync_status, ...}
  const [fhirWriteConfig, setFhirWriteConfig] = useState(null); // {can_write, ...}
  const [fhirWriteConfirm, setFhirWriteConfirm] = useState(null); // {type: 'problems'|'medications'|'allergies'}

  // Dictation recorder hook (real MediaRecorder + Whisper)
  const dictation = useDictationRecorder({
    appMode,
    onTranscriptReady: (transcript, method, sid) => {
      // Feed real transcript into SOAP generation path
      if (transcript) {
        setTranscript([{ time: '0:00', speaker: 'Transcript', text: transcript }]);
      }
    },
    onError: (msg) => {
      toast.error(msg);
    },
  });

  // Entry transition — find and reuse any existing nav overlay, or create one
  const [showTransition, setShowTransition] = useState(true);
  const [autoStartDone, setAutoStartDone] = useState(false);
  useEffect(() => {
    // Remove any navigation overlay from the previous page
    const navOverlay = document.getElementById('nav-transition-overlay');
    if (navOverlay) {
      navOverlay.style.transition = 'opacity 500ms ease-out';
      navOverlay.style.opacity = '0';
      setTimeout(() => navOverlay.remove(), 520);
    }
    // Fade out our own overlay
    const timer = setTimeout(() => setShowTransition(false), 100);
    return () => clearTimeout(timer);
  }, []);


  const startTour = () => { setTourStep(0); setTourDismissed(false); sessionStorage.removeItem('cf-tour-dismissed'); };
  const nextTour = () => { if (tourStep < TOUR_STEPS.length - 1) setTourStep(tourStep + 1); else { setTourStep(-1); setTourDismissed(true); sessionStorage.setItem('cf-tour-dismissed', 'true'); } };
  const skipTour = () => { setTourStep(-1); setTourDismissed(true); sessionStorage.setItem('cf-tour-dismissed', 'true'); };

  // ── Schedule syncs with selected specialty (demo switcher) ──
  useEffect(() => {
    if (isRecording) return; // never swap mid-recording
    const specialtySchedule = getScheduleForSpecialty(specialtyId);
    const dateKey = selectedDate.toISOString().slice(0, 10);
    const todayKey = new Date().toISOString().slice(0, 10);
    if (specialtySchedule) {
      setSchedule(specialtySchedule);
    } else {
      setSchedule(dateKey === todayKey ? TODAYS_SCHEDULE : (FUTURE_SCHEDULES[dateKey] || []));
    }
    // Clear any active patient selection so the demo roster feels fresh.
    setActiveApt(null); setPatient(null); setTranscript([]); setSoapContent(null);
    setRecordingMode(null); setEditingSections({}); setElapsed(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialtyId]);

  const handleReturn = () => {
    // Exit transition
    const overlay = document.getElementById('cf-transition-overlay');
    if (overlay) {
      overlay.style.transitionDuration = '300ms';
      overlay.style.opacity = '1';
      setTimeout(() => {
        toast('Clinical Focus session ended', { className: 'bg-black text-white', duration: 2000 });
        navigate(-1);
      }, 320);
    } else {
      toast('Clinical Focus session ended', { duration: 2000 });
      navigate(-1);
    }
  };

  /* ── Chart Document Upload handlers ── */

  // Timer
  useEffect(() => {
    if (isRecording && !isPaused) { intervalRef.current = setInterval(() => setElapsed(e => e + 1), 1000); }
    else { clearInterval(intervalRef.current); }
    return () => clearInterval(intervalRef.current);
  }, [isRecording, isPaused]);

  // Auto-select patient from URL params (e.g., ?patientId=P001 or ?patientId=<vendor-uuid>)
  useEffect(() => {
    const pid = searchParams.get('patientId');
    if (!pid || activeApt) return;

    // 1) Check mock schedule
    const aptMatch = schedule.find(a => a.patientId === pid);
    if (aptMatch) {
      setActiveApt(aptMatch);
      setPatient(getPatient(aptMatch));
      return;
    }

    // 2) Check mock PATIENTS_DB
    if (PATIENTS_DB[pid]) {
      const p = PATIENTS_DB[pid];
      const synthApt = { id: `DIRECT-${pid}`, patientId: pid, name: `${p.firstName} ${p.lastName}`, time: 'Direct', type: 'Chart Review', chief: 'Patient chart opened from roster', status: 'in_progress' };
      setSchedule(prev => prev.some(a => a.patientId === pid) ? prev : [synthApt, ...prev]);
      setActiveApt(synthApt);
      setPatient(p);
      return;
    }

    // 3) Fetch real patient from vendor API
    setPatientLoading(true);
    setPatientError(null);
    (async () => {
      try {
        const res = await authFetch(`${API}/api/patients/${pid}`);
        if (res.ok) {
          const data = await res.json();
          const fn = data.first_name || data.firstName || '';
          const ln = data.last_name || data.lastName || '';
          const fullName = `${fn} ${ln}`.trim() || 'Unknown Patient';

          const realPatient = {
            firstName: fn, lastName: ln,
            age: data.age || '',
            gender: data.gender || data.sex || '',
            dob: data.date_of_birth || data.dob || data.dateOfBirth || '',
            mrn: data.mrn || data.medical_record_number || data.id || pid,
            insurance: data.payer_name || data.insurance || '',
            memberId: data.member_id || data.memberId || '',
            phone: data.phone || data.phone_number || '',
            email: data.email || '',
            address: [data.street, data.city, data.state, data.zip_code].filter(Boolean).join(', '),
            emergencyContact: null,
            pcp: data.pcp || data.primary_care_provider || 'Not assigned',
            preferredPharmacy: data.pharmacy || '',
            vitals: data.vitals || { bp: '—', hr: '—', temp: '—', spo2: '—', weight: '—', bmi: '—' },
            problems: data.problems || data.conditions || [],
            medications: data.medications || [],
            allergies: data.allergies || [],
            labs: data.labs || data.lab_results || [],
            soapNotes: data.soap_notes || data.soapNotes || [],
            orders: data.orders || [],
            referrals: data.referrals || [],
            documents: data.documents || [],
            riskAdjustment: data.risk_adjustment || data.riskAdjustment || { rafScore: 0, category: 'Not assessed', hccCategories: [], gapOpportunities: [] },
            careCoordination: data.care_coordination || data.careCoordination || { ccm: { eligible: false }, rpm: { eligible: false }, awv: { eligible: false }, apcm: { eligible: false } },
            actionableItems: data.actionable_items || data.actionableItems || [],
            aiBrief: data.ai_brief || data.aiBrief || { summary: `${fullName} — chart loaded from patient roster`, keyFindings: [], attentionItems: [] },
          };
          const synthApt = { id: `VENDOR-${pid}`, patientId: pid, name: fullName, time: 'Direct', type: 'Chart Review', chief: 'Patient chart opened from roster', status: 'in_progress' };
          setSchedule(prev => prev.some(a => a.patientId === pid) ? prev : [synthApt, ...prev]);
          setActiveApt(synthApt);
          setPatient(realPatient);
        } else if (res.status === 404) {
          setPatientError('Patient not found');
          toast.error('Patient not found');
        } else if (res.status === 401) {
          setPatientError('Authentication required');
          toast.error('Please sign in to view patient details');
        } else {
          const err = await res.json().catch(() => ({}));
          setPatientError(err.detail || `Failed to load patient (${res.status})`);
          toast.error(err.detail || 'Failed to load patient');
        }
      } catch {
        setPatientError('Network error loading patient');
        toast.error('Network error loading patient');
      }
      setPatientLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Auto-start ambient session if ?autoStart=ambient is present
  useEffect(() => {
    if (autoStartDone || !patient || !activeApt) return;
    const mode = searchParams.get('autoStart');
    if (mode === 'ambient' || mode === 'dictation') {
      const timer = setTimeout(() => {
        startSession(mode);
        setAutoStartDone(true);
      }, 600);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient, activeApt, autoStartDone, searchParams]);

  // Transcript simulation
  useEffect(() => {
    if (isRecording && !isPaused && elapsed > 0 && elapsed % 5 === 0) {
      const idx = Math.min(Math.floor(elapsed / 5) - 1, LIVE_TRANSCRIPT.length - 1);
      if (idx >= 0 && idx < LIVE_TRANSCRIPT.length) {
        setTranscript(prev => prev.length <= idx ? [...prev, LIVE_TRANSCRIPT[idx]] : prev);
      }
    }
  }, [elapsed, isRecording, isPaused]);

  // ── Smart Prep: Fetch cached → if miss/stale, generate via LLM ──
  const fetchSmartPrep = useCallback(async (forceRefresh = false) => {
    if (!patient || !activeApt) { setSmartPrep(null); return; }
    setSmartPrepLoading(true);
    setSmartPrepError(null);
    if (forceRefresh) setSmartPrep(null);
    try {
      const context = {
        patient_id: activeApt.patientId || patient.mrn || '',
        patient_name: `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
        chief_complaint: activeApt.chief || '',
        visit_type: activeApt.type || '',
        medical_history: (patient.problems || []).map(p => typeof p === 'string' ? p : p.name || p.condition || JSON.stringify(p)).join(', '),
        medications: (patient.medications || []).map(m => typeof m === 'string' ? m : m.name || m.drug || JSON.stringify(m)).join(', '),
        allergies: (patient.allergies || []).map(a => typeof a === 'string' ? a : `${a.allergen || a.name || ''}${a.severity ? ` (${a.severity})` : ''}`).join(', '),
        vitals: patient.vitals ? Object.entries(patient.vitals).filter(([,v]) => v && v !== '—').map(([k,v]) => `${k}: ${v}`).join(', ') : '',
        prior_notes: (patient.soapNotes || []).slice(0, 2).map(n => typeof n === 'string' ? n : n.summary || n.assessment || '').join(' | '),
      };
      const data = await LuminaService.generateSmartPrep(context, forceRefresh);
      if (data.success && data.prep) {
        setSmartPrep({ ...data.prep, _source: data.source, _cachedAt: data.cached_at });
      } else {
        setSmartPrepError(data.detail || 'No Smart Prep available');
      }
    } catch (err) {
      const msg = err?.message || 'Smart Prep generation failed';
      // Demo fallback — ONLY wire the canned Smart Prep fixture in demo
      // mode. Real-mode API failures must surface as an error banner so
      // clinicians are never shown fabricated clinical summaries.
      if (isDemo && activeApt?.patientId === 'P001') {
        setSmartPrep({
          visitNumber: '4th',
          reason: 'Diabetes management',
          summary: 'John Smith presents for diabetes management follow-up. Focus on glycemic control, current medications, and cardiovascular risk — complication screening is overdue.',
          keyMetrics: [
            { label: 'HbA1c', value: '7.2%', target: '<7.0%', status: 'above' },
            { label: 'Blood pressure', value: '138/88', target: '<130/80', status: 'above' },
            { label: 'LDL-C', value: '98 mg/dL', target: '<100', status: 'at_target' },
            { label: 'Urine alb/Cr', value: 'Unknown', target: '<30', status: 'unknown' },
          ],
          openItems: [
            'Titrate Metformin or add GLP-1 — A1c above target',
            'Annual diabetic eye exam overdue',
            'Check urine albumin/creatinine ratio',
          ],
          lastVisit: '2025-12-15',
          _source: 'demo',
        });
      } else {
        setSmartPrepError(msg);
      }
      console.warn('[SmartPrep]', msg);
    } finally {
      setSmartPrepLoading(false);
    }
  }, [patient, activeApt, isDemo]);

  useEffect(() => {
    let cancelled = false;
    if (!patient || !activeApt) { setSmartPrep(null); return; }
    const run = async () => {
      await fetchSmartPrep(false);
    };
    run();
    return () => { cancelled = true; };
  }, [patient, activeApt, fetchSmartPrep]);

  // ── FHIR EHR sync: fetch status + write config when patient changes ──
  useEffect(() => {
    if (!patient) { setFhirStatus(null); setFhirWriteConfig(null); return; }
    const fetchFhirStatus = async () => {
      try {
        const status = await svc.fhir.getSyncStatus(patient.mrn || activeApt?.patientId);
        setFhirStatus(status);
      } catch {
        setFhirStatus(null);
      }
      try {
        const wc = await svc.fhir.getWriteConfig('provider');
        setFhirWriteConfig(wc);
      } catch {
        setFhirWriteConfig(null);
      }
    };
    fetchFhirStatus();
  }, [patient, activeApt, svc]);

  const handleFhirSync = useCallback(async () => {
    if (!patient) return;
    const patId = patient.mrn || activeApt?.patientId;
    try {
      setFhirStatus(prev => prev ? { ...prev, sync_status: 'syncing' } : { fhir_enabled: true, sync_status: 'syncing' });
      await svc.fhir.syncPatient(patId);
      toast.success('EHR sync started');
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const status = await svc.fhir.getSyncStatus(patId);
          setFhirStatus(status);
          if (status.sync_status !== 'syncing') {
            clearInterval(poll);
            if (status.sync_status === 'synced' || status.sync_status === 'partial') {
              toast.success('EHR data synced');
            }
          }
        } catch { clearInterval(poll); }
      }, 2000);
      // Safety timeout: stop polling after 30s
      setTimeout(() => clearInterval(poll), 30000);
    } catch {
      toast.error('EHR sync failed');
      setFhirStatus(prev => prev ? { ...prev, sync_status: 'failed' } : null);
    }
  }, [patient, activeApt, svc.fhir]);

  const handleFhirWrite = useCallback(async (resourceType) => {
    setFhirWriteConfirm({ type: resourceType });
  }, []);

  const confirmFhirWrite = useCallback(async () => {
    if (!fhirWriteConfirm || !patient) return;
    const { type } = fhirWriteConfirm;
    const patId = patient.mrn || activeApt?.patientId;
    const role = 'provider';
    setFhirWriteConfirm(null);

    try {
      const items = type === 'problems' ? patient.problems : type === 'medications' ? patient.medications : patient.allergies;
      if (!items?.length) return;

      let successCount = 0;
      for (const item of items) {
        if (item._source === 'fhir') continue; // skip items already from FHIR
        const key = `${patId}-${type}-${item.name || item.allergen}-${Date.now()}`;
        const writeFn = type === 'problems' ? svc.fhir.writeProblem
          : type === 'medications' ? svc.fhir.writeMedication
          : svc.fhir.writeAllergy;
        const payload = type === 'problems' ? { name: item.name, icd: item.icd || '', onset: item.onset || '', idempotency_key: key }
          : type === 'medications' ? { name: item.name, freq: item.freq || '', prescriber: item.prescriber || '', startDate: item.startDate || '', idempotency_key: key }
          : { allergen: item.allergen, reaction: item.reaction || '', severity: item.severity || 'Mild', idempotency_key: key };
        const res = await writeFn(patId, payload, role);
        if (res.success) successCount++;
      }
      toast.success(`Pushed ${successCount} ${type} to EHR`);
    } catch {
      toast.error(`Failed to push ${type} to EHR`);
    }
  }, [fhirWriteConfirm, patient, activeApt, svc.fhir.writeAllergy, svc.fhir.writeMedication, svc.fhir.writeProblem]);

  // ── Load persisted schedule imports on mount ──
  useEffect(() => {
    const loadPersistedSchedule = async () => {
      try {
        const data = await LuminaService.getLatestSchedule();
        if (data.success && data.appointments && data.appointments.length > 0) {
          setSchedule(prev => {
            const existingIds = new Set(prev.map(a => a.patientId));
            const newApts = data.appointments.filter(a => !existingIds.has(a.patientId));
            return newApts.length > 0 ? [...prev, ...newApts] : prev;
          });
        }
      } catch {
        // No persisted schedule — use default TODAYS_SCHEDULE (already set)
      }
    };
    loadPersistedSchedule();
  }, []);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // ── Handlers ──
  const handleDateChange = (date) => {
    const dateKey = date.toISOString().slice(0, 10);
    const todayKey = new Date().toISOString().slice(0, 10);
    if (isRecording) { toast.error('Stop recording first'); return; }
    setSelectedDate(date);
    setActiveApt(null); setPatient(null); setTranscript([]); setSoapContent(null); setElapsed(0);
    setSchedule(dateKey === todayKey ? TODAYS_SCHEDULE : (FUTURE_SCHEDULES[dateKey] || []));
  };

  const handleSelectPatient = (apt) => {
    if (isRecording) { toast.error('Stop recording before switching patients'); return; }
    setActiveApt(apt);
    // Prefer specialty-specific patient record (peds/psych/obgyn/surgical) when available.
    setPatient(getSpecialtyPatient(apt) || getPatient(apt));
    setTranscript([]); setSoapContent(null); setElapsed(0); setRecordingMode(null); setEditingSections({});
    setActiveTab('soap');
  };

  const startSession = (mode) => {
    setRecordingMode(mode); setIsRecording(true); setIsPaused(false); setElapsed(0); setTranscript([]); setSoapContent(null);
    // Real audio capture when dictation is enabled
    if (dictation.dictationEnabled) {
      dictation.start(
        activeApt?.patientId || patient?.mrn || '',
        `${patient?.firstName} ${patient?.lastName}`,
        mode,
        activeApt?.chief || '',
      );
    }
    toast.success(`${mode === 'ambient' ? 'Ambient listening' : 'Dictation'} started`, { description: `${patient?.firstName} ${patient?.lastName}` });
  };

  const stopSession = useCallback(async () => {
    setIsRecording(false); setIsPaused(false); setIsGenerating(true);

    // ── Fast demo path — skip real API, show fallback SOAP almost instantly.
    // User explicitly requested mocked/fast SOAP for demos.
    if (isDemo) {
      await new Promise(r => setTimeout(r, 800));
      setIsGenerating(false); setSoapContent({ ...FALLBACK_SOAP }); setRecordingMode(null);
      toast.success('SOAP note generated');
      return;
    }

    toast.success('Session complete — generating SOAP note...');

    // If real dictation is active, stop + finalize first to get transcript
    let realTranscriptText = '';
    if (dictation.dictationEnabled && dictation.sessionId) {
      try {
        await dictation.stop();
        // Wait a tick for final chunk to queue
        await new Promise(r => setTimeout(r, 500));
        const result = await dictation.finalize();
        // finalize triggers onTranscriptReady, but we also grab directly
        if (dictation.finalTranscript) {
          realTranscriptText = dictation.finalTranscript;
        }
      } catch {
        // Fall through to SOAP with whatever transcript we have
      }
    }

    const transcriptForSoap = realTranscriptText
      || (transcript.length > 0 ? transcript.map(l => l.text).join('. ') : '');

    const fallbackTimer = setTimeout(() => {
      setIsGenerating(false); setSoapContent({ ...FALLBACK_SOAP }); setRecordingMode(null);
      toast.success('SOAP note generated');
    }, 15000);

    try {
      const res = await fetch(`${API}/api/lumina/soap/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_name: `${patient?.firstName} ${patient?.lastName}`,
          chief_complaint: transcriptForSoap || (activeApt?.chief || 'Follow-up visit'),
          age: String(patient?.age || ''), sex: patient?.gender || '',
          medical_history: patient?.problems?.map(p => p.name).join(', ') || '',
          medications: patient?.medications?.map(m => m.name).join(', ') || '',
        }),
      });
      clearTimeout(fallbackTimer);
      const data = await res.json();
      setIsGenerating(false);
      setSoapContent(data.success && data.soap ? { ...data.soap } : { ...FALLBACK_SOAP });
      setRecordingMode(null);
      toast.success('SOAP note generated');
    } catch {
      clearTimeout(fallbackTimer);
      setIsGenerating(false); setSoapContent({ ...FALLBACK_SOAP }); setRecordingMode(null);
      toast.success('SOAP note generated');
    }
  }, [patient, transcript, activeApt, dictation, isDemo]);

  // Auto-generate after stop
  useEffect(() => {
    if (!isRecording && recordingMode && transcript.length > 0 && !soapContent && !isGenerating) {
      const t = setTimeout(stopSession, 800);
      return () => clearTimeout(t);
    }
  }, [isRecording, recordingMode, transcript, soapContent, isGenerating, stopSession]);

  const handleCopyFull = () => {
    if (!soapContent) return;
    requestCopy(`SOAP Note — ${patient?.firstName} ${patient?.lastName}\n${new Date().toLocaleDateString()}\n\nSUBJECTIVE:\n${soapContent.subjective}\n\nOBJECTIVE:\n${soapContent.objective}\n\nASSESSMENT:\n${soapContent.assessment}\n\nPLAN:\n${soapContent.plan}`, 'Full SOAP Note');
  };
  const handleCopyAP = () => { if (soapContent) requestCopy(`ASSESSMENT:\n${soapContent.assessment}\n\nPLAN:\n${soapContent.plan}`, 'A&P'); };
  const handleCopyCodes = () => { requestCopy(SUGGESTED_CODES.map(c => `${c.code} – ${c.desc}`).join('\n'), 'Billing Codes'); };

  const handleDownloadCCDA = () => {
    if (!soapContent) return;
    const name = `${patient?.firstName}_${patient?.lastName}`;
    const ccda = `<?xml version="1.0" encoding="UTF-8"?>\n<ClinicalDocument xmlns="urn:hl7-org:v3"><title>SOAP Note - ${name}</title><component><structuredBody>
    <component><section><title>Subjective</title><text>${soapContent.subjective}</text></section></component>
    <component><section><title>Objective</title><text>${soapContent.objective}</text></section></component>
    <component><section><title>Assessment</title><text>${soapContent.assessment}</text></section></component>
    <component><section><title>Plan</title><text>${soapContent.plan}</text></section></component>
    </structuredBody></component></ClinicalDocument>`;
    const blob = new Blob([ccda], { type: 'application/xml' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = `SOAP_${name}_${new Date().toISOString().slice(0, 10)}.xml`; a.click(); URL.revokeObjectURL(url);
    toast.success('CCDA downloaded');
  };

  const handleDoneWithNote = () => { setSoapContent(null); setEditingSections({}); setActiveTab('soap'); };
  const onCancelRecording = () => {
    setRecordingMode(null); setIsRecording(false); setIsPaused(false); setElapsed(0); setTranscript([]);
    if (dictation.dictationEnabled && dictation.sessionId) dictation.cancel();
  };

  const handleReviewNote = (note) => {
    setReviewingNote(note);
    setReviewEdits({ subjective: note.soap?.subjective || '', objective: note.soap?.objective || '', assessment: note.soap?.assessment || '', plan: note.soap?.plan || '' });
    setEditingSections({});
    setReviewDictating(null);
  };

  // ── Render ──
  return (
    <div className="h-screen flex flex-col bg-[#F8FAFC]" data-testid="unified-workspace">
      <Toaster position="top-right" />

      {/* Entry/Exit Transition Overlay — only shows if no nav-transition-overlay exists */}
      <div id="cf-transition-overlay"
        className="fixed inset-0 bg-black z-[100] pointer-events-none"
        style={{ opacity: showTransition ? 0 : 0, transition: 'opacity 300ms ease-out' }}
      />

      {/* ── Top Bar — Black banner for Clinical Focus (FULL WIDTH) ── */}
      <div className="h-12 shrink-0 bg-black flex items-center justify-between px-5 z-50 relative" data-testid="cf-topbar">
        <div className="flex items-center gap-3">
          {recordingMode && !isGenerating && !soapContent && patient ? (
            <div className="flex items-center gap-3 px-3 py-1.5 rounded-full bg-red-500/15 border border-red-500/30" data-testid="recording-pill">
              <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs font-semibold text-red-400">{recordingMode === 'ambient' ? 'Ambient' : 'Dictation'}</span>
              <div className="h-3 w-px bg-white/15" />
              <span className="text-xs text-white font-medium">{patient.firstName} {patient.lastName}</span>
              <div className="h-3 w-px bg-white/15" />
              <span className="text-sm font-mono font-bold text-white tabular-nums">{formatTime(elapsed)}</span>
              <div className="h-3 w-px bg-white/15" />
              <span className="text-[10px] text-white/50">~{Math.ceil(elapsed / 60)} credits</span>
            </div>
          ) : (
            <>
              <div className="h-5 w-5 rounded bg-white/15 flex items-center justify-center p-0.5">
                <img src={LUMINA_EMBLEM} alt="Lumina" className="h-full w-full object-contain invert" />
              </div>
              <span className="text-sm font-bold text-white">Lumina</span>
              <div className="h-4 w-px bg-white/15" />
              <span className="text-xs text-white/50">Clinical Focus</span>
              {patient && (
                <>
                  <ChevronRight className="h-3 w-3 text-white/30" />
                  <span className="text-xs text-white font-medium">{patient.firstName} {patient.lastName}</span>
                </>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Tour Badge (matching dashboard style) */}
          <Badge variant="outline" className="gap-1 px-2.5 py-1 text-[10px] font-medium cursor-pointer hover:bg-white/10 border-white/20 text-white transition-colors"
            onClick={startTour} data-testid="cf-tour-btn" title="Clinical Focus tour">
            <Compass className="h-2.5 w-2.5" /> Tour
          </Badge>
          <Button size="sm" variant="ghost" className="h-8 text-[11px] text-white hover:text-white hover:bg-white/10 gap-1" onClick={() => navigate(`/lumina/lens?patientId=${activeApt?.patientId || patient?.mrn || ''}`)} data-testid="cf-lens-btn">
            <Camera className="h-3.5 w-3.5" /> Lens
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-[11px] text-white hover:text-white hover:bg-white/10 gap-1" data-testid="cf-assist-btn">
            <Sparkles className="h-3.5 w-3.5" /> Assist
          </Button>
          {isDemo && <SpecialtySwitcher />}
          <div className="h-4 w-px bg-white/15" />
          <Badge variant="outline" className="gap-1 px-2 py-1 text-[10px] font-semibold border-amber-500/40 text-amber-400 cursor-pointer hover:bg-white/10" data-testid="cf-credits-badge">
            <Coins className="h-3 w-3" /> 247 ($247)
          </Badge>
          <span className="text-[9px] text-white/40 font-medium">HIPAA &#10003;</span>
          <div className="h-4 w-px bg-white/15" />
          <NotificationBell />
          <Button size="sm" variant="ghost" className="text-xs text-white hover:text-white hover:bg-white/10 gap-1 h-8" onClick={handleReturn} data-testid="return-btn">
            <ArrowLeft className="h-3 w-3" /> Return
          </Button>
          <div className="h-7 w-7 rounded-full bg-white/15 flex items-center justify-center text-[10px] font-bold text-white">SC</div>
        </div>
      </div>

      {/* ── Below banner: Sidebar + Content ── */}
      <div className="flex flex-1 min-h-0">
        {/* ── Patient Sidebar ── */}
        <ClinicalFocusSidebar
          schedulePatients={schedule}
          allPatients={ALL_PATIENTS_LIST}
          activePatientId={activeApt?.patientId}
          onSelectPatient={handleSelectPatient}
          collapsed={scheduleCollapsed}
          onToggle={() => setScheduleCollapsed(!scheduleCollapsed)}
          onUploadSchedule={() => setShowScheduleLens(true)}
        />

        {/* ── Main Workspace ── */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden" style={{ background: '#f5f5f5' }}>

        {/* Content Area */}
        <div className={cn("flex-1", recordingMode && !isGenerating && !soapContent && patient ? "flex overflow-hidden" : "overflow-y-auto")}>
          {!patient ? (
            /* ── Empty / Loading / Error State ── */
            <div className="flex flex-col items-center justify-center h-full text-center px-8" data-testid="empty-state">
              {patientLoading ? (
                <>
                  <Loader2 className="h-10 w-10 animate-spin text-slate-300 mb-4" />
                  <p className="text-lg font-semibold text-slate-700">Loading patient...</p>
                  <p className="text-sm text-slate-400 mt-1">Fetching patient details from the system</p>
                </>
              ) : patientError ? (
                <>
                  <div className="h-16 w-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
                    <AlertTriangle className="h-7 w-7 text-red-400" />
                  </div>
                  <p className="text-lg font-semibold text-slate-700" data-testid="patient-error-message">{patientError}</p>
                  <p className="text-sm text-slate-400 mt-1">Unable to load patient chart. Check the patient ID or try again.</p>
                  <Button variant="outline" className="mt-4" onClick={() => navigate('/lumina/patients')}>Back to Patients</Button>
                </>
              ) : (
                <>
                  <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                    <Stethoscope className="h-7 w-7 text-slate-300" />
                  </div>
                  <p className="text-lg font-semibold text-slate-700">Select a patient to begin</p>
                  <p className="text-sm text-slate-400 mt-1 max-w-md">Choose a patient from the schedule to view their AI-generated brief, start an ambient visit, or review documentation.</p>
                  <p className="text-xs text-slate-300 mt-3">No prior chart data? Lens can extract history from referral letters or prior visit notes in seconds.</p>
                </>
              )}
            </div>
          ) : recordingMode && !isGenerating && !soapContent ? (
            /* ══════════ SPLIT-PANE RECORDING LAYOUT ══════════ */
            <RecordingPanel
              patient={patient}
              activeApt={activeApt}
              isRecording={isRecording}
              isPaused={isPaused}
              elapsed={dictation.dictationEnabled ? dictation.elapsed : elapsed}
              transcript={transcript}
              recordingMode={recordingMode}
              stopSession={stopSession}
              formatTime={formatTime}
              setIsPaused={(val) => {
                setIsPaused(val);
                if (dictation.dictationEnabled && dictation.sessionId) {
                  if (val) dictation.pause(); else dictation.resume();
                }
              }}
              onCancelRecording={onCancelRecording}
              recordingTab={recordingTab}
              setRecordingTab={setRecordingTab}
              uploadedDocs={uploadedDocs}
              setUploadedDocs={setUploadedDocs}
              clinicalChecklist={clinicalChecklist}
              setClinicalChecklist={setClinicalChecklist}
              smartPrep={smartPrep}
              smartPrepLoading={smartPrepLoading}
              smartPrepError={smartPrepError}
              fetchSmartPrep={fetchSmartPrep}
              dictation={dictation.dictationEnabled ? dictation : null}
              lensService={svc.lens}
            />
          ) : (
            <div className="p-4 max-w-5xl mx-auto" style={{ background: '#f5f5f5' }}>

              {/* ── Patient Header ── */}
              <PatientHeaderBar
                patient={patient}
                recordingMode={recordingMode}
                isGenerating={isGenerating}
                soapContent={soapContent}
                onShowLens={() => setShowLens(true)}
                onStartSession={startSession}
                fhirStatus={fhirStatus}
                onFhirSync={handleFhirSync}
              />
              {/* ── Smart Prep — Compact Card ── */}
              {patient && !soapContent && !recordingMode && !isGenerating && (
                <div style={{ marginBottom: 12 }}>
                  <SmartPrepCard
                    prep={smartPrep}
                    loading={smartPrepLoading}
                    error={smartPrepError}
                    onRetry={() => fetchSmartPrep(true)}
                    onRefresh={() => fetchSmartPrep(true)}
                    visitContext={activeApt ? `${activeApt.visitNumber || 'N/A'} visit · ${activeApt.chief || ''}` : ''}
                  />
                </div>
              )}

              {/* ── Condition Blocks with Smart Expand ── */}
              {patient && !soapContent && !recordingMode && !isGenerating && (
                <div style={{ marginBottom: 12 }}>
                  <DiseaseBlocksPanel patientId={activeApt?.patientId} visitType={activeApt?.chief} />
                </div>
              )}

              {/* ── Filter Bar ── */}
              {!soapContent && !recordingMode && !isGenerating && (
                <div className="sticky top-0 z-10 pb-2" style={{ background: '#f5f5f5' }} data-testid="chart-filter-bar">
                  <div className="flex items-center gap-1 p-1 rounded-xl bg-slate-100/80 backdrop-blur-sm w-fit">
                    <button onClick={() => setChartView('overview')} className={cn("px-4 py-1.5 rounded-lg text-xs font-semibold transition-all", chartView === 'overview' ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")} data-testid="filter-overview">
                      Overview
                    </button>
                    <button onClick={() => setChartView('chart')} className={cn("px-4 py-1.5 rounded-lg text-xs font-semibold transition-all", chartView === 'chart' ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")} data-testid="filter-full-chart">
                      Full Patient Chart
                    </button>
                  </div>
                  {absorbedData?.absorption_item_count > 0 && (
                    <Badge variant="outline" className="text-[9px] border-teal-200 text-teal-700 bg-teal-50 gap-0.5 ml-2 h-6" data-testid="absorbed-data-badge-dm">
                      <ArrowUpToLine className="h-2.5 w-2.5" /> Includes absorbed data
                    </Badge>
                  )}
                  {absorbedData?.patient_id && absorbedData.patient_id !== (activeApt?.patientId || patient?.mrn) && absorbedData.absorption_item_count > 0 && (
                    <div className="flex items-center gap-1.5 ml-2 px-2 py-1 rounded bg-amber-50 border border-amber-200" data-testid="patient-mismatch-warning">
                      <AlertTriangle className="h-3 w-3 text-amber-600 flex-shrink-0" />
                      <span className="text-[9px] text-amber-700 font-medium">Chart patient differs from absorbed patient ({absorbedData.patient_id})</span>
                    </div>
                  )}
                  {chartView === 'chart' && (
                    <div className="flex items-center gap-1 mt-2 overflow-x-auto pb-1">
                      {[
                        { val: 'demographics', label: 'Demographics' }, { val: 'problem-list', label: 'Problems' },
                        { val: 'medications', label: 'Medications' }, { val: 'allergies', label: 'Allergies' },
                        { val: 'lab-results', label: 'Lab Results' }, { val: 'vitals-detail', label: 'Vitals' },
                        { val: 'notes', label: 'Notes' }, { val: 'orders', label: 'Orders' },
                        { val: 'referrals', label: 'Referrals' }, { val: 'documents', label: 'Documents' },
                      ].map(t => (
                        <button key={t.val} onClick={() => setChartTab(t.val)} className={cn("px-3 py-1 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all border", chartTab === t.val ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300")} data-testid={`chart-tab-${t.val}`}>
                          {t.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ══════════════════════ OVERVIEW VIEW ══════════════════════ */}
              {!soapContent && !recordingMode && !isGenerating && chartView === 'overview' && (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={cardOrder} strategy={verticalListSortingStrategy}>
                    <div className="space-y-3" data-testid="overview-view">
                      {cardOrder.map(cardId => {
                        if (cardId === 'specialty-intake') {
                          const card = (
                            <SpecialtyIntakeCard
                              patient={patient}
                              specialtyMode={specialty.mode}
                              onScanWithLens={(demoKey) => { setLensDefaultDemo(demoKey); setShowLens(true); }}
                            />
                          );
                          if (!card || (card && card.type && !patient.pedsIntake && !patient.psychIntake && !patient.obgynIntake && !patient.surgicalIntake)) return null;
                          return (
                            <SortableCard key={cardId} id={cardId}>
                              {card}
                            </SortableCard>
                          );
                        }
                        if (cardId === 'quick-glance') return (
                          <SortableCard key={cardId} id={cardId}>
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="quick-glance-row">
                              {/* Chief Complaint */}
                              {activeApt && (
                                <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="chief-complaint-card">
                                  <div className="flex items-center gap-2 mb-1.5">
                                    <div className="h-6 w-6 rounded-md bg-amber-50 flex items-center justify-center shrink-0"><Clipboard className="h-3 w-3 text-amber-600" /></div>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Chief Complaint</p>
                                  </div>
                                  <p className="text-sm font-semibold text-slate-800 leading-tight">{activeApt.chief}</p>
                                  <p className="text-[10px] text-slate-400 mt-1">{activeApt.type} · {activeApt.time}</p>
                                </div>
                              )}
                              {/* Medications Quick View */}
                              <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="meds-quick-card">
                                <div className="flex items-center justify-between mb-1.5">
                                  <div className="flex items-center gap-2">
                                    <div className="h-6 w-6 rounded-md bg-blue-50 flex items-center justify-center shrink-0"><Pill className="h-3 w-3 text-blue-500" /></div>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Medications</p>
                                  </div>
                                  <Badge className="bg-slate-100 text-slate-500 text-[8px] border-0">{patient.medications?.length || 0}</Badge>
                                </div>
                                <div className="space-y-0.5">
                                  {patient.medications?.map((m, i) => (
                                    <p key={i} className="text-[11px] text-slate-700 truncate"><span className="font-medium">{m.name}</span> <span className="text-slate-400">{m.freq}</span></p>
                                  ))}
                                  {(!patient.medications || patient.medications.length === 0) && <p className="text-[10px] text-slate-400 italic">None</p>}
                                </div>
                              </div>
                              {/* Lab Results Quick View */}
                              <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="labs-quick-card">
                                <div className="flex items-center justify-between mb-1.5">
                                  <div className="flex items-center gap-2">
                                    <div className="h-6 w-6 rounded-md bg-amber-50 flex items-center justify-center shrink-0"><TestTube className="h-3 w-3 text-amber-500" /></div>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Lab Results</p>
                                  </div>
                                  <Badge className="bg-slate-100 text-slate-500 text-[8px] border-0">{patient.labs?.length || 0}</Badge>
                                </div>
                                <div className="space-y-0.5">
                                  {patient.labs?.slice(0, 3).map((l, i) => (
                                    <p key={i} className={cn("text-[11px] truncate", l.flag ? "text-red-600 font-medium" : "text-slate-700")}>{l.test}: <span className="font-semibold">{l.value}</span></p>
                                  ))}
                                  {patient.labs?.length > 3 && <p className="text-[10px] text-slate-400">+{patient.labs.length - 3} more</p>}
                                  {(!patient.labs || patient.labs.length === 0) && <p className="text-[10px] text-slate-400 italic">None</p>}
                                </div>
                              </div>
                            </div>
                          </SortableCard>
                        );

                        if (cardId === 'vitals' && patient.vitals) return (
                          <SortableCard key={cardId} id={cardId}>
                            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="vitals-dashboard">
                              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                                <div className="flex items-center gap-2"><Heart className="h-4 w-4 text-red-400" /><p className="text-xs font-bold text-slate-800">Vitals</p></div>
                                <p className="text-[10px] text-slate-400">Last recorded: Today</p>
                              </div>
                              <div className="grid grid-cols-3 lg:grid-cols-6 gap-px bg-slate-100">
                                {[
                                  { label: 'Blood Pressure', value: patient.vitals.bp, unit: 'mmHg', color: 'text-violet-600' },
                                  { label: 'Heart Rate', value: patient.vitals.hr, unit: 'bpm', color: 'text-rose-600' },
                                  { label: 'SpO2', value: patient.vitals.spo2 + '%', unit: '', color: 'text-sky-600' },
                                  { label: 'Temperature', value: patient.vitals.temp, unit: '°F', color: 'text-amber-600' },
                                  { label: 'Weight', value: patient.vitals.weight, unit: '', color: 'text-emerald-600' },
                                  { label: 'BMI', value: patient.vitals.bmi, unit: '', color: 'text-indigo-600' },
                                ].map(v => (
                                  <div key={v.label} className="bg-white p-3 text-center">
                                    <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">{v.label}</p>
                                    <p className={cn("text-lg font-bold mt-1", v.color)}>{v.value}</p>
                                    {v.unit && <p className="text-[9px] text-slate-400">{v.unit}</p>}
                                  </div>
                                ))}
                              </div>
                              {patient.vitalsHistory && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-100">
                                  <div className="bg-white p-4">
                                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Blood Pressure Trend</p>
                                    <ResponsiveContainer width="100%" height={120}>
                                      <AreaChart data={patient.vitalsHistory} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                                        <defs><linearGradient id="bpSysGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.3} /><stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} /></linearGradient><linearGradient id="bpDiaGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366F1" stopOpacity={0.15} /><stop offset="100%" stopColor="#6366F1" stopOpacity={0} /></linearGradient></defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" /><XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} /><YAxis domain={[60, 160]} tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                        <RTooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                                        <Area type="monotone" dataKey="systolic" stroke="#8B5CF6" strokeWidth={2} fill="url(#bpSysGrad)" name="Systolic" /><Area type="monotone" dataKey="diastolic" stroke="#6366F1" strokeWidth={1.5} fill="url(#bpDiaGrad)" name="Diastolic" />
                                      </AreaChart>
                                    </ResponsiveContainer>
                                  </div>
                                  <div className="bg-white p-4">
                                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Heart Rate Trend</p>
                                    <ResponsiveContainer width="100%" height={120}>
                                      <AreaChart data={patient.vitalsHistory} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                                        <defs><linearGradient id="hrGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F43F5E" stopOpacity={0.3} /><stop offset="100%" stopColor="#F43F5E" stopOpacity={0} /></linearGradient></defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" /><XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} /><YAxis domain={[55, 95]} tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                        <RTooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }} /><Area type="monotone" dataKey="hr" stroke="#F43F5E" strokeWidth={2} fill="url(#hrGrad)" name="HR" dot={{ r: 3, fill: '#F43F5E' }} />
                                      </AreaChart>
                                    </ResponsiveContainer>
                                  </div>
                                  <div className="bg-white p-4">
                                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">SpO2 Trend</p>
                                    <ResponsiveContainer width="100%" height={120}>
                                      <AreaChart data={patient.vitalsHistory} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                                        <defs><linearGradient id="spo2Grad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.3} /><stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} /></linearGradient></defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" /><XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} /><YAxis domain={[93, 100]} tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                        <RTooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }} /><Area type="monotone" dataKey="spo2" stroke="#0EA5E9" strokeWidth={2} fill="url(#spo2Grad)" name="SpO2 %" dot={{ r: 3, fill: '#0EA5E9' }} />
                                      </AreaChart>
                                    </ResponsiveContainer>
                                  </div>
                                  <div className="bg-white p-4">
                                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Weight & BMI Trend</p>
                                    <ResponsiveContainer width="100%" height={120}>
                                      <LineChart data={patient.vitalsHistory} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" /><XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                        <YAxis yAxisId="w" domain={[175, 195]} tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} /><YAxis yAxisId="b" orientation="right" domain={[25, 30]} tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} hide />
                                        <RTooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                                        <Line yAxisId="w" type="monotone" dataKey="weight" stroke="#10B981" strokeWidth={2} name="Weight (lbs)" dot={{ r: 3, fill: '#10B981' }} /><Line yAxisId="b" type="monotone" dataKey="bmi" stroke="#6366F1" strokeWidth={1.5} strokeDasharray="4 4" name="BMI" dot={{ r: 2, fill: '#6366F1' }} />
                                      </LineChart>
                                    </ResponsiveContainer>
                                  </div>
                                </div>
                              )}
                            </div>
                          </SortableCard>
                        );

                        if (cardId === 'risk-care' && (patient.riskAdjustment?.rafScore > 0.5 || Object.values(patient.careCoordination || {}).some(v => v?.eligible))) return (
                          <SortableCard key={cardId} id={cardId}>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="risk-care-section">
                              {patient.riskAdjustment?.rafScore > 0.5 && (
                                <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="risk-adjustment-card">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-slate-500" /><p className="text-xs font-bold text-slate-800">Risk Adjustment (HCC)</p></div>
                                    <div className="flex items-center gap-2"><p className="text-lg font-bold text-slate-900">{patient.riskAdjustment.rafScore}</p><Badge className={cn("text-[9px] border-0", patient.riskAdjustment.category === 'High' ? 'bg-red-100 text-red-700' : patient.riskAdjustment.category === 'Moderate' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600')}>{patient.riskAdjustment.category} RAF</Badge></div>
                                  </div>
                                  {patient.riskAdjustment.hccCategories?.length > 0 && (
                                    <div className="space-y-1.5 mb-3">{patient.riskAdjustment.hccCategories.map((hcc, i) => (
                                      <div key={i} className="flex items-center justify-between text-xs"><div className="flex items-center gap-2"><Badge variant="outline" className="text-[9px] font-mono">{hcc.code}</Badge><span className="text-slate-600">{hcc.desc}</span></div><span className={cn("font-mono text-[10px]", hcc.weight > 0 ? "text-slate-800" : "text-amber-600")}>{hcc.weight > 0 ? `+${hcc.weight}` : 'Gap'}</span></div>
                                    ))}</div>
                                  )}
                                  {patient.riskAdjustment.gapOpportunities?.length > 0 && (
                                    <div className="border-t border-slate-100 pt-2 space-y-1">{patient.riskAdjustment.gapOpportunities.map((gap, i) => (
                                      <p key={i} className="text-[10px] text-amber-700 flex items-start gap-1"><TrendingUp className="h-3 w-3 shrink-0 mt-0.5" />{gap}</p>
                                    ))}</div>
                                  )}
                                </div>
                              )}
                              {Object.values(patient.careCoordination || {}).some(v => v?.eligible) && (
                                <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="care-coordination-card">
                                  <div className="flex items-center gap-2 mb-3"><HeartPulse className="h-4 w-4 text-emerald-500" /><p className="text-xs font-bold text-slate-800">Care Coordination</p></div>
                                  <div className="space-y-2">
                                    {[
                                      { key: 'ccm', label: 'Chronic Care Mgmt (CCM)', icon: ClipboardList, color: 'bg-blue-50 border-blue-200 text-blue-700' },
                                      { key: 'rpm', label: 'Remote Patient Monitoring', icon: Wifi, color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
                                      { key: 'awv', label: 'Annual Wellness Visit', icon: ShieldCheck, color: 'bg-violet-50 border-violet-200 text-violet-700' },
                                      { key: 'apcm', label: 'Advanced Primary Care Mgmt', icon: Zap, color: 'bg-amber-50 border-amber-200 text-amber-700' },
                                    ].filter(p => patient.careCoordination?.[p.key]?.eligible).map(program => {
                                      const PIcon = program.icon;
                                      const data = patient.careCoordination[program.key];
                                      return (
                                        <div key={program.key} className={cn("p-2.5 rounded-lg border flex items-start gap-2.5", program.color)} data-testid={`care-${program.key}`}>
                                          <PIcon className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                          <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                          <p className="text-xs font-semibold">{program.label}</p>
                                          <Button size="sm" variant="outline" className="h-5 text-[8px] px-2 font-semibold gap-1 border-current" onClick={() => toast.success(`Enrolled in ${program.label}`)} data-testid={`enroll-${program.key}`}>
                                            <Plus className="h-2.5 w-2.5" /> Enroll
                                          </Button>
                                        </div>
                                            <p className="text-[10px] opacity-80 mt-0.5">{data.reason}</p>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </SortableCard>
                        );

                        if (cardId === 'actionable' && patient.actionableItems?.length > 0) return (
                          <SortableCard key={cardId} id={cardId}>
                            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="actionable-items-card">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2"><Lightbulb className="h-4 w-4 text-amber-500" /><p className="text-xs font-bold text-slate-800">Actionable Clinical Items</p><Badge className="bg-slate-100 text-slate-500 text-[9px] border-0">Evidence-based</Badge></div>
                                {patient.actionableItems.filter(i => i.priority === 'High').length > 0 && (
                                  <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1.5 border-red-200 text-red-700 hover:bg-red-50" onClick={() => toast.success('All urgent items added to SOAP')} data-testid="add-all-urgent-btn">
                                    <Plus className="h-3 w-3" /> Add all urgent to SOAP
                                  </Button>
                                )}
                              </div>
                              <div className="space-y-2">{patient.actionableItems.map(item => (
                                <div key={item.id} className={cn("flex items-start gap-3 p-2.5 rounded-lg border", item.priority === 'High' ? 'border-red-100 bg-red-50/30' : 'border-slate-100 bg-slate-50/30')} data-testid={`actionable-${item.id}`}>
                                  <Badge className={cn("text-[8px] border-0 shrink-0 mt-0.5", item.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700')}>
                                    {item.priority === 'High' ? 'P1 · Urgent' : 'P2 · Review'}
                                  </Badge>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2"><Badge variant="outline" className="text-[8px]">{item.category}</Badge></div>
                                    <p className="text-xs text-slate-800 font-medium mt-1">{item.action}</p>
                                    <p className="text-[10px] text-slate-400 mt-0.5 italic">{item.evidence}</p>
                                  </div>
                                  {item.forSoap && (
                                    <button onClick={() => toast.success('Added to SOAP', { description: item.action.slice(0, 60) })}
                                      className="text-[9px] font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors shrink-0"
                                      data-testid={`add-soap-${item.id}`}>+ SOAP</button>
                                  )}
                                </div>
                              ))}</div>
                            </div>
                          </SortableCard>
                        );

                        return null;
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              {/* ══════════════════════ FULL PATIENT CHART VIEW ══════════════════════ */}
              {!soapContent && !recordingMode && !isGenerating && chartView === 'chart' && (
                <ChartTabSections
                  patient={patient}
                  chartTab={chartTab}
                  chartUploads={chartUploads}
                  chartFileRef={chartFileRef}
                  onChartUpload={handleChartUpload}
                  onChartAbsorb={handleChartAbsorb}
                  onNavigateToSettings={() => navigate('/lumina/settings?tab=documents')}
                  onReviewNote={handleReviewNote}
                  canWriteFhir={fhirWriteConfig?.can_write === true}
                  onFhirWrite={handleFhirWrite}
                  patientDocuments={patientDocuments}
                  absorbedData={absorbedData}
                />
              )}


              {/* Recording panel now handled by split-pane layout above */}

              {/* ── Generating ── */}
              {isGenerating && (
                <div className="rounded-xl border border-slate-200 bg-white p-6 flex items-center justify-center gap-4" data-testid="generating-spinner">
                  <Loader2 className="h-6 w-6 text-slate-400 animate-spin" />
                  <div>
                    <p className="font-semibold text-sm text-slate-700">Generating SOAP Note...</p>
                    <p className="text-xs text-slate-400">Analyzing conversation and clinical data</p>
                  </div>
                </div>
              )}

              {/* ── SOAP Note View ── */}
              {soapContent && specialty.mode === 'mental' && (
                <MentalHealthPanel
                  patient={patient}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  transcript={transcript}
                  onDone={handleDoneWithNote}
                  requestCopy={requestCopy}
                />
              )}
              {soapContent && specialty.mode === 'surgical' && (
                <SurgicalPanel patient={patient} onDone={handleDoneWithNote} />
              )}
              {soapContent && specialty.mode === 'soap+obgyn' && <OBGYNPanel />}
              {soapContent && specialty.mode === 'soap+peds'  && <PediatricsPanel />}
              {soapContent && (specialty.mode === 'soap' || specialty.mode === 'soap+obgyn' || specialty.mode === 'soap+peds') && (
                <SOAPPanel
                  soapContent={soapContent}
                  setSoapContent={setSoapContent}
                  patient={patient}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  editingSections={editingSections}
                  setEditingSections={setEditingSections}
                  transcript={transcript}
                  requestCopy={requestCopy}
                  onCopyFull={handleCopyFull}
                  onCopyAP={handleCopyAP}
                  onCopyCodes={handleCopyCodes}
                  onDownloadCCDA={handleDownloadCCDA}
                  onShowDM={() => setShowDM(true)}
                  onShowInstructions={() => setShowInstructions(true)}
                  onShowSummary={() => setShowSummary(true)}
                  onShowBilling={() => setShowBilling(true)}
                  onDone={handleDoneWithNote}
                  onReviewNote={handleReviewNote}
                />
              )}


            </div>
          )}
        </div>
      </div>{/* end flex-1 flex-col main workspace */}
      </div>{/* end flex flex-1 sidebar+content */}

      {/* ── Dialogs ── */}
      <ScheduleLensDialog open={showScheduleLens} onOpenChange={setShowScheduleLens} onAppointmentsExtracted={apts => setSchedule(prev => [...prev, ...apts])} />
      <LensDemoDialog
        open={showLens}
        onOpenChange={(v) => { setShowLens(v); if (!v) setLensDefaultDemo(null); }}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : ''}
        defaultDemoKey={lensDefaultDemo}
      />
      <SendToBillingDialog open={showBilling} onOpenChange={setShowBilling} context="Coding & claim data from SOAP note" />

      <DoctorDialogs
        patient={patient}
        soapContent={soapContent}
        showDM={showDM} setShowDM={setShowDM}
        dmRecipient={dmRecipient} setDmRecipient={setDmRecipient}
        showCoding={showCoding} setShowCoding={setShowCoding}
        showInstructions={showInstructions} setShowInstructions={setShowInstructions}
        showSummary={showSummary} setShowSummary={setShowSummary}
        summaryEmail={summaryEmail} setSummaryEmail={setSummaryEmail}
        reviewingNote={reviewingNote} setReviewingNote={setReviewingNote}
        reviewEdits={reviewEdits} setReviewEdits={setReviewEdits}
        editingSections={editingSections} setEditingSections={setEditingSections}
        reviewDictating={reviewDictating} setReviewDictating={setReviewDictating}
        requestCopy={requestCopy}
        onCopyCodes={handleCopyCodes}
        setShowLens={setShowLens}
      />

      <HIPAADialog />
      <LuminaChatbot patient={patient} transcript={transcript} />

      {/* FHIR Write Confirmation Dialog */}
      <Dialog open={!!fhirWriteConfirm} onOpenChange={(v) => !v && setFhirWriteConfirm(null)}>
        <DialogContent className="sm:max-w-md" data-testid="fhir-write-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">Push {fhirWriteConfirm?.type} to EHR?</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            This will write local {fhirWriteConfirm?.type} data to the connected FHIR server. Items already sourced from EHR will be skipped.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setFhirWriteConfirm(null)} data-testid="fhir-write-cancel">Cancel</Button>
            <Button size="sm" onClick={confirmFhirWrite} className="bg-violet-600 hover:bg-violet-700" data-testid="fhir-write-confirm">Push to EHR</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tour System */}
      <TourOverlay
        tourActive={tourActive}
        tourStep={tourStep}
        setTourStep={setTourStep}
        skipTour={skipTour}
        nextTour={nextTour}
      />
    </div>
  );
};

export default LuminaDoctorMode;
;
